import { useEffect, useState, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Buildings,
  Calendar,
  EnvelopeSimple,
  PaperPlaneRight,
  Phone,
  SpinnerGap,
  Paperclip,
  DownloadSimple,
  Info,
  X,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { ADMIN_TOKEN_KEY } from './AdminLoginPage';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminRequestDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Delivery form state
  const [deliveryFiles, setDeliveryFiles] = useState([]);
  const [deliveryMessage, setDeliveryMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Info needed state
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [infoMessage, setInfoMessage] = useState('');
  const [sendingInfo, setSendingInfo] = useState(false);

  const fileInputRef = useRef(null);

  const token = localStorage.getItem(ADMIN_TOKEN_KEY);
  const authHeaders = { Authorization: `Bearer ${token}` };

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/service-requests/${id}`,
        { headers: authHeaders }
      );
      if (res.status === 401) {
        navigate('/admin/login');
        return;
      }
      if (!res.ok) throw new Error('فشل التحميل');
      setData(await res.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleDeliver = async (e) => {
    e.preventDefault();
    if (deliveryFiles.length === 0) {
      toast.error('يرجى رفع ملف التسليم');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('message', deliveryMessage);
      deliveryFiles.forEach((f) => fd.append('files', f));
      const res = await fetch(
        `${API_URL}/api/admin/service-requests/${id}/deliver`,
        {
          method: 'POST',
          headers: authHeaders,
          body: fd,
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'فشل الإرسال');
      }
      toast.success('تم الإرسال للعميل ✅');
      setDeliveryFiles([]);
      setDeliveryMessage('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      await refresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleInfoNeeded = async () => {
    if (!infoMessage.trim()) {
      toast.error('اكتبي ما تحتاجين توضيحه');
      return;
    }
    setSendingInfo(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/service-requests/${id}/info-needed`,
        {
          method: 'POST',
          headers: { ...authHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify({ message: infoMessage.trim() }),
        }
      );
      if (!res.ok) throw new Error('فشل الإرسال');
      toast.success('تم إرسال الطلب للعميل');
      setShowInfoModal(false);
      setInfoMessage('');
      await refresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSendingInfo(false);
    }
  };

  if (loading || !data) {
    return (
      <main className="pt-24 min-h-screen bg-[#F7F5F2] flex items-center justify-center">
        <SpinnerGap size={36} className="animate-spin text-[#1F3D2B]" />
      </main>
    );
  }

  return (
    <main
      data-testid="admin-detail-page"
      className="pt-24 pb-16 min-h-screen bg-[#F7F5F2]"
      dir="rtl"
    >
      <div className="max-w-4xl mx-auto px-4 space-y-5">
        <Link
          to="/admin/requests"
          className="inline-flex items-center gap-1.5 text-sm text-[#7A866E] hover:text-[#1F3D2B] mb-2"
        >
          <ArrowRight size={16} />
          العودة للقائمة
        </Link>

        {/* Header card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-[20px] shadow-sm border border-[#E6E0D6] p-6"
        >
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="bg-[#F5F2EC] text-[#3D512D] text-xs font-bold px-3 py-1 rounded-full">
              {data.service_label}
            </span>
            <span className="text-xs text-[#7A866E]">#{data.id.slice(0, 8)}</span>
          </div>
          <h1 className="text-2xl font-bold text-[#1F3D2B] mb-1 font-['IBM_Plex_Sans_Arabic']">
            {data.name}
          </h1>
          <p className="text-[#7A866E] mb-4 flex items-center gap-1.5">
            <Buildings size={16} /> {data.organization}
          </p>
          <div className="grid sm:grid-cols-2 gap-3 text-sm text-[#1F3D2B]">
            <div className="flex items-center gap-2">
              <EnvelopeSimple size={16} className="text-[#4A641F]" />
              <span dir="ltr">{data.email}</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone size={16} className="text-[#4A641F]" />
              <span dir="ltr">{data.phone}</span>
            </div>
            <div className="flex items-center gap-2 sm:col-span-2">
              <Calendar size={16} className="text-[#4A641F]" />
              <span>{new Date(data.created_at).toLocaleString('ar-SA')}</span>
            </div>
          </div>
          {data.notes && (
            <div className="mt-4 bg-[#F5F2EC] rounded-xl p-4 border-r-4 border-[#4A641F]">
              <p className="text-xs text-[#7A866E] mb-1">ملاحظات العميل</p>
              <p className="text-[#1F3D2B] leading-8 whitespace-pre-line">
                {data.notes}
              </p>
            </div>
          )}
        </motion.div>

        {/* Client files */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white rounded-[20px] shadow-sm border border-[#E6E0D6] p-6"
        >
          <h2 className="font-bold text-[#1F3D2B] mb-3 flex items-center gap-2">
            <Paperclip size={18} className="text-[#4A641F]" />
            ملفات العميل ({data.files?.length || 0})
          </h2>
          {!data.files || data.files.length === 0 ? (
            <p className="text-sm text-[#7A866E]">لم يرفع العميل أي ملف</p>
          ) : (
            <div className="space-y-2">
              {data.files.map((f, idx) => (
                <a
                  key={idx}
                  href={`${API_URL}${f.download_url}?token=${token}`}
                  download={f.filename}
                  data-testid={`download-client-file-${idx}`}
                  onClick={async (e) => {
                    e.preventDefault();
                    const r = await fetch(`${API_URL}${f.download_url}`, {
                      headers: authHeaders,
                    });
                    const blob = await r.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = f.filename;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="flex items-center justify-between p-3 bg-[#F5F2EC] rounded-xl hover:bg-[#EBE6DC] transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <DownloadSimple size={20} className="text-[#4A641F] shrink-0" />
                    <span className="text-sm text-[#1F3D2B] truncate">
                      {f.filename}
                    </span>
                  </div>
                  <span className="text-xs text-[#7A866E] shrink-0">
                    {Math.round(f.size / 1024)} KB
                  </span>
                </a>
              ))}
            </div>
          )}
        </motion.div>

        {/* Existing deliveries */}
        {data.deliverable_files && data.deliverable_files.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-[#F5F2EC] rounded-[20px] border border-[#E6E0D6] p-6"
          >
            <h2 className="font-bold text-[#1F3D2B] mb-2">
              ✅ تم التسليم — {new Date(data.delivered_at).toLocaleString('ar-SA')}
            </h2>
            {data.delivery_message && (
              <p className="text-[#3D512D] leading-7 whitespace-pre-line mb-3">
                {data.delivery_message}
              </p>
            )}
            <div className="space-y-2">
              {data.deliverable_files.map((f, idx) => (
                <p key={idx} className="text-sm text-[#1F3D2B] flex items-center gap-2">
                  <DownloadSimple size={14} className="text-[#4A641F]" />
                  {f.filename}{' '}
                  <span className="text-xs text-[#7A866E]">
                    ({Math.round(f.size / 1024)} KB)
                  </span>
                </p>
              ))}
            </div>
          </motion.div>
        )}

        {/* Delivery form */}
        <motion.form
          onSubmit={handleDeliver}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-white rounded-[20px] shadow-sm border border-[#E6E0D6] p-6"
        >
          <h2 className="font-bold text-[#1F3D2B] mb-4 font-['IBM_Plex_Sans_Arabic']">
            رفع التسليم وإرسال للعميل
          </h2>

          <label
            htmlFor="deliv-files"
            className="block border-2 border-dashed border-[#D8D2C8] rounded-2xl p-5 text-center cursor-pointer hover:border-[#4A641F] mb-3"
          >
            <Paperclip size={24} className="mx-auto text-[#7A866E] mb-1" />
            <p className="text-sm font-medium text-[#1F3D2B]">
              اختاري ملفات التسليم (PDF/Word/Excel/صور)
            </p>
            <input
              id="deliv-files"
              type="file"
              ref={fileInputRef}
              multiple
              data-testid="deliverable-files-input"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.webp,.heic"
              className="sr-only"
              onChange={(e) => setDeliveryFiles(Array.from(e.target.files))}
            />
          </label>
          {deliveryFiles.length > 0 && (
            <ul className="space-y-1 mb-3">
              {deliveryFiles.map((f, idx) => (
                <li key={idx} className="text-sm text-[#1F3D2B] flex items-center gap-2">
                  <Paperclip size={14} className="text-[#4A641F]" />
                  {f.name} <span className="text-xs text-[#7A866E]">({Math.round(f.size / 1024)} KB)</span>
                </li>
              ))}
            </ul>
          )}

          <Textarea
            data-testid="delivery-message-input"
            value={deliveryMessage}
            onChange={(e) => setDeliveryMessage(e.target.value)}
            rows={4}
            placeholder="رسالة مخصّصة للعميل (اختياري)"
            className="rounded-xl mb-4"
          />

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              type="submit"
              data-testid="send-to-client-btn"
              disabled={submitting}
              className="flex-1 bg-[#1F3D2B] hover:bg-[#163020] text-white rounded-xl py-3 flex items-center justify-center gap-2"
            >
              <PaperPlaneRight size={18} />
              {submitting ? 'جاري الإرسال...' : 'إرسال التقرير للعميل'}
            </Button>
            <Button
              type="button"
              data-testid="open-info-modal-btn"
              onClick={() => setShowInfoModal(true)}
              variant="outline"
              className="border-[#D8D2C8] text-[#1F3D2B] rounded-xl py-3 flex items-center justify-center gap-2"
            >
              <Info size={18} />
              طلب توضيح
            </Button>
          </div>
        </motion.form>
      </div>

      {/* Info needed modal */}
      {showInfoModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => !sendingInfo && setShowInfoModal(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="bg-white rounded-[20px] max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
            dir="rtl"
          >
            <div className="flex justify-between items-start mb-3">
              <h3 className="font-bold text-[#1F3D2B] font-['IBM_Plex_Sans_Arabic']">
                طلب توضيح من العميل
              </h3>
              <button onClick={() => setShowInfoModal(false)}>
                <X size={20} className="text-[#7A866E]" />
              </button>
            </div>
            <p className="text-sm text-[#7A866E] mb-3 leading-7">
              راح يصل العميل رسالة بنبرة ودودة:<br />
              <em className="text-[#3D512D]">
                «أثناء المراجعة، احتجنا بعض التفاصيل البسيطة عشان يكون التحليل أدق.»
              </em>
            </p>
            <Textarea
              data-testid="info-needed-textarea"
              value={infoMessage}
              onChange={(e) => setInfoMessage(e.target.value)}
              rows={5}
              placeholder="اكتبي ما تحتاجين توضيحه من العميل"
              className="rounded-xl mb-4"
            />
            <Button
              onClick={handleInfoNeeded}
              data-testid="send-info-needed-btn"
              disabled={sendingInfo}
              className="w-full bg-[#1F3D2B] hover:bg-[#163020] text-white rounded-xl py-3"
            >
              {sendingInfo ? 'جاري الإرسال...' : 'إرسال للعميل'}
            </Button>
          </motion.div>
        </motion.div>
      )}
    </main>
  );
};

export default AdminRequestDetailPage;
